// components/RecoveryKeyDisplay.js
import React, { useState } from 'react';
import { Shield, AlertCircle, CheckCircle } from 'lucide-react';

const RecoveryKeyDisplay = ({
  recoveryKey,
  projectName = 'App',
  userEmail = '',
  onConfirm,
  confirmButtonText = 'Continue',
  brandColor = 'slate',
  showWarning = true,
  requireConfirmation = true
}) => {
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyDownloaded, setKeyDownloaded] = useState(false);
  const [confirmSaved, setConfirmSaved] = useState(false);
  const [error, setError] = useState('');

  // Color scheme based on brandColor prop
  const colorSchemes = {
    slate: {
      gradient: 'from-slate-900 via-slate-800 to-slate-900',
      button: 'from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800'
    },
    blue: {
      gradient: 'from-blue-900 via-blue-800 to-blue-900',
      button: 'from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600'
    },
    green: {
      gradient: 'from-green-900 via-green-800 to-green-900',
      button: 'from-green-600 to-green-700 hover:from-green-500 hover:to-green-600'
    },
    purple: {
      gradient: 'from-purple-900 via-purple-800 to-purple-900',
      button: 'from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600'
    }
  };

  const colors = colorSchemes[brandColor] || colorSchemes.slate;

  const copyRecoveryKey = () => {
    navigator.clipboard.writeText(recoveryKey).then(() => {
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 3000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = recoveryKey;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 3000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    });
  };

  const downloadRecoveryKey = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${projectName.toLowerCase().replace(/\s+/g, '-')}-recovery-key-${timestamp}.txt`;
    
    const content = [
      `${projectName} Recovery Key`,
      '='.repeat(50),
      '',
      'IMPORTANT: Keep this key safe!',
      'You will need it to reset your password.',
      '',
      'Recovery Key:',
      recoveryKey,
      '',
      `Generated: ${new Date().toLocaleString()}`,
      ...(userEmail ? [`Account: ${userEmail}`] : []),
      '',
      'Security Tips:',
      '- Store this in a password manager',
      '- Save it in a secure location',
      '- Do NOT share it with anyone',
      '- Do NOT store it in email or cloud storage',
      '',
      '='.repeat(50)
    ].join('\n');
    
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    setKeyDownloaded(true);
  };

  const handleConfirm = () => {
    if (requireConfirmation && !confirmSaved) {
      setError('Please confirm that you have saved your recovery key by checking the box');
      return;
    }
    
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.gradient} flex items-center justify-center p-4`}>
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Save Your Recovery Key
            </h2>
            <p className="text-gray-600">
              This is the ONLY time you'll see this key. Store it securely!
            </p>
          </div>

          {showWarning && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Why is this important?</p>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Your data is encrypted with your password</li>
                    <li>• If you forget your password, this key is the ONLY way to recover</li>
                    <li>• We cannot reset your password without this key</li>
                    <li>• Lost key = permanently locked out of your account</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <p className="text-xs text-gray-600 font-semibold mb-2">YOUR RECOVERY KEY:</p>
                <div className="font-mono text-sm break-all text-gray-900 leading-relaxed select-all">
                  {recoveryKey}
                </div>
              </div>
            </div>
          )}

          {!showWarning && (
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
              <p className="text-xs text-gray-600 font-semibold mb-2">YOUR RECOVERY KEY:</p>
              <div className="font-mono text-sm break-all text-gray-900 leading-relaxed select-all">
                {recoveryKey}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={copyRecoveryKey}
              className={`flex items-center justify-center gap-2 ${
                keyCopied ? 'bg-green-100 text-green-800' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              } py-3 px-4 rounded-lg font-medium transition-all`}
            >
              {keyCopied ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy Key
                </>
              )}
            </button>
            <button
              onClick={downloadRecoveryKey}
              className={`flex items-center justify-center gap-2 ${
                keyDownloaded ? 'bg-green-100 text-green-800' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              } py-3 px-4 rounded-lg font-medium transition-all`}
            >
              {keyDownloaded ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Downloaded!
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </>
              )}
            </button>
          </div>

          {requireConfirmation && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmSaved}
                  onChange={(e) => setConfirmSaved(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  I confirm that I have saved my recovery key in a secure location and understand that losing it means I cannot recover my account
                </span>
              </label>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3 mb-4">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {onConfirm && (
            <button
              onClick={handleConfirm}
              disabled={requireConfirmation && !confirmSaved}
              className={`w-full bg-gradient-to-r ${
                (!requireConfirmation || confirmSaved) ? colors.button : 'from-gray-400 to-gray-500'
              } text-white py-4 px-6 rounded-xl font-semibold shadow-lg transition-all duration-200 ${
                (!requireConfirmation || confirmSaved) ? 'transform hover:scale-105' : 'cursor-not-allowed'
              }`}
            >
              {confirmButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecoveryKeyDisplay;